import { useState, useEffect } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  FieldPath,
  useWatch,
} from 'react-hook-form';
import { QueryKey } from '@tanstack/react-query';
import { cn } from '@/lib/utils.ts';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputHint from '@/components/form/base/InputHint.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import PrimitiveAsyncSelect from '@/components/asyncSelect/PrimitiveAsyncSelect.tsx';
import useAsyncSelectOptions from '@/components/asyncSelect/useAsyncSelectOptions.ts';
import Option from '@/components/form/types/Option.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { PagedDto } from '@/components/table/types/PagedDto.ts';
import { Props as SelectProps } from 'react-select';

export type AsyncSearchSelectProps<T extends FieldValues, TData> = {
  fieldPathName: FieldPath<T>;
  control: Control<T>;
  queryKey: QueryKey;
  fetchFn: (
    pageRequest: PageRequest,
    filter: FilterCriteriaDto,
  ) => Promise<PagedDto<TData>>;
  toOption: (item: TData) => Option;
  label?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  className?: string;
  pageSize?: number;
  idFieldId?: string;
  onChange?: (value: string | object | undefined) => void;
  /**
   * `false` gdy filtr, od którego zależy lista, jest jeszcze pusty — select nie
   * odpytuje wtedy backendu ani przy montowaniu, ani przy otwarciu menu.
   */
  enabled?: boolean;
  /** Zmiana którejkolwiek wartości czyści cache i doczytuje pierwszą stronę. */
  cacheUniqs?: unknown[];
} & Omit<
  SelectProps<Option, false>,
  | 'value'
  | 'onChange'
  | 'isMulti'
  | 'name'
  | 'inputId'
  | 'options'
  | 'onInputChange'
  | 'loadOptions'
>;

const AsyncSearchSelectRHF = <T extends FieldValues, TData>({
  fieldPathName,
  control,
  queryKey,
  fetchFn,
  toOption,
  label,
  required = false,
  hint,
  error,
  className,
  pageSize = 20,
  idFieldId,
  onChange,
  enabled = true,
  cacheUniqs,
  ...props
}: AsyncSearchSelectProps<T, TData>) => {
  const [initialOption, setInitialOption] = useState<Option | undefined>();
  const { loadOptions, fetchOptionById } = useAsyncSelectOptions({
    queryKey,
    fetchFn,
    toOption,
    pageSize,
    idFieldId,
    enabled,
  });

  const currentValue = useWatch({ control, name: fieldPathName });

  useEffect(() => {
    if (enabled && currentValue && !initialOption) {
      fetchOptionById(currentValue as string).then((option) => {
        if (option) setInitialOption(option);
      });
    }
  }, [enabled, currentValue, fetchOptionById, initialOption]);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <InputLabel
          label={label}
          required={required}
          disabled={props.isDisabled}
        />
      )}
      <Controller
        control={control}
        name={fieldPathName}
        render={({ field }) => (
          <PrimitiveAsyncSelect
            {...props}
            mobileMenuTitle={props.mobileMenuTitle ?? label}
            name={fieldPathName}
            id={fieldPathName}
            error={error}
            loadOptions={loadOptions}
            enabled={enabled}
            cacheUniqs={cacheUniqs}
            value={field.value}
            initialOption={initialOption}
            onChange={(value) => {
              field.onChange(value);
              onChange?.(value);
            }}
          />
        )}
      />
      {error && <InputError error={error} />}
      {hint && <InputHint hint={hint} />}
    </div>
  );
};

export default AsyncSearchSelectRHF;
